import { Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Icon } from '@/components/Icon';
import { useSession } from '@/session/SessionProvider';
import { SignOutButton } from '@/features/auth/SignOutButton';
import { navFor, PHONE_TABS, type NavEntry } from '@/app/nav';
import type { Tier } from '@/lib/tier';
import logoUrl from '@/assets/aware-logo.svg';
import './AppShell.css';

function NavLink({
  entry,
  active,
  locked,
  className,
  showLabel,
}: {
  entry: NavEntry;
  active: boolean;
  locked: boolean;
  className: string;
  showLabel: 'label' | 'tab';
}) {
  return (
    <Link
      to={locked ? '/pricing' : entry.to}
      className={className}
      data-active={active}
      data-locked={locked}
      aria-label={locked ? `${entry.label} — locked, see plans` : undefined}
    >
      <Icon name={entry.icon} />
      <span className="nav__label">{showLabel === 'tab' ? entry.tab : entry.label}</span>
      {locked && <Icon name="lock" className="nav__lock" />}
    </Link>
  );
}

/**
 * Routes that render WITHOUT the product chrome.
 *
 * These are the screens a signed-out person sees. Wrapping them in the shell put the sidebar,
 * the tab bar and the tier switcher around the sign-in form — a stranger looking at half a
 * logged-in application, with navigation to places they cannot go. It also leaked the shape of
 * the product to anyone hitting the URL.
 *
 * Matched by prefix so `/callback?code=…` and any future sub-path stay bare too.
 */
const BARE_ROUTES = ['/', '/sign-in', '/callback', '/signup', '/plan-pricing', '/checkout'] as const;

function isBareRoute(pathname: string): boolean {
  return BARE_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
}

export function AppShell() {
  const { tier, setTier, loading, needsPlan } = useSession();
  const { isAuthenticated, isLoading } = useKindeAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = navFor(tier);

  // Pre-auth screens: the logo, and nothing else. No nav, no tabs, no tier switcher.
  if (isBareRoute(pathname)) {
    return (
      <div className="shell shell--bare">
        <header className="shell__bareHeader">
          <img className="shell__logo" src={logoUrl} alt="Aware" />
        </header>
        <main className="shell__bareMain">
          <Outlet />
        </main>
        {/* On every signed-out screen, including checkout. Someone being asked for a card is
            exactly the person entitled to read the terms first, and the payment provider
            requires these reachable from the domain it is asked to approve. */}
        <footer className="shell__legal">
          <a href="/terms">Terms of Service</a>
          <span aria-hidden="true">·</span>
          <a href="/privacy">Privacy Policy</a>
          <span aria-hidden="true">·</span>
          <a href="mailto:support@awarenow.ai">support@awarenow.ai</a>
        </footer>
      </div>
    );
  }

  // THE GATE. Every route that is not explicitly bare requires a signed-in user.
  //
  // This is not what protects the data — the API validates the bearer token on every request
  // and is the only check that holds. What this protects is coherence: without it the product
  // chrome renders for a stranger, every panel inside it fails its own fetch with a 401, and
  // nothing anywhere invites them to sign in. They see a broken application instead of a door.
  //
  // `isLoading` is held blank rather than treated as signed-out. Kinde restores the session
  // asynchronously on a refresh, and redirecting during that window would bounce an
  // already-authenticated user out of the page they were looking at.
  if (isLoading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" search={{ returnTo: pathname }} replace />;
  }

  // Signed in, but nothing paid for. Sign-in is free on every connection this application
  // offers — passwordless code or social — so identity alone entitles nobody to the product.
  // They go to the plan chooser, not to a feed.
  //
  // `loading` is held blank rather than sent onward: the entitlement arrives a moment after
  // the token, and redirecting in that gap would bounce a paying subscriber to the plan
  // chooser on every cold load.
  if (loading) return null;
  if (needsPlan && pathname !== '/plan-pricing') {
    return <Navigate to="/plan-pricing" replace />;
  }

  const isActive = (entry: NavEntry) => pathname.startsWith(entry.to);
  const phoneTabs = nav.main.slice(0, PHONE_TABS);
  const hasMore = nav.moreRows.length > 0;

  return (
    <div className="shell">
      <header className="shell__header">
        <Link to="/feed" aria-label="Aware Insights home">
          <img className="shell__logo" src={logoUrl} alt="Aware" />
        </Link>
      </header>

      <nav className="shell__sidebar" aria-label="Main">
        <Link to="/feed" aria-label="Aware Insights home">
          <img className="shell__logo" src={logoUrl} alt="Aware" />
        </Link>

        <div className="shell__nav">
          {nav.main.map((entry) => (
            <NavLink
              key={entry.id}
              entry={entry}
              active={isActive(entry)}
              locked={false}
              className="nav__item"
              showLabel="label"
            />
          ))}
        </div>

        {/* Locked entries stay visible in their own group and route to pricing — the resident
            tier is the top of the upgrade funnel, not a stripped-down product. */}
        {nav.locked.length > 0 && (
          <div className="shell__nav shell__nav--locked">
            <p className="u-meta shell__navHeading">Included in higher plans</p>
            {nav.locked.map((entry) => (
              <NavLink
                key={entry.id}
                entry={entry}
                active={false}
                locked
                className="nav__item"
                showLabel="label"
              />
            ))}
          </div>
        )}

        {/* Development affordance, compiled out of production builds: `import.meta.env.DEV`
            is statically false there, so this whole block is removed rather than hidden.
            Shipping it would let any visitor render the Pro navigation. */}
        {import.meta.env.DEV && (
          <div className="shell__devbar">
            <label htmlFor="dev-tier">Preview tier (dev only)</label>
            <select id="dev-tier" value={tier} onChange={(e) => setTier(e.target.value as Tier)}>
              <option value="snapshot">Snapshot</option>
              <option value="intelligence">Intelligence</option>
              <option value="pro">Intelligence Pro</option>
            </select>
          </div>
        )}

        <div className="shell__account">
          <SignOutButton />
        </div>
      </nav>

      <main className="shell__main">
        <Outlet />
      </main>

      <nav className="shell__tabs" aria-label="Main">
        {phoneTabs.map((entry) => (
          <NavLink
            key={entry.id}
            entry={entry}
            active={isActive(entry)}
            locked={false}
            className="nav__tab"
            showLabel="tab"
          />
        ))}
        {hasMore && (
          <Link to="/more" className="nav__tab" data-active={pathname.startsWith('/more')}>
            <Icon name="more" />
            <span className="nav__label">More</span>
          </Link>
        )}
      </nav>
    </div>
  );
}
